import { useEffect, useState, useCallback } from 'react';

const API_URL = import.meta.env.VITE_API_URL || '';

interface Zeile {
  id: string;
  name: string;
  gesamt: number;
  verbrauch: number;
}

interface Antwort {
  from: string;
  to: string;
  boxen: Zeile[];
}

interface Props {
  onClose: () => void;
  token: string;
}

function heute(): string {
  return new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' });
}
function jahresanfang(): string {
  return `${heute().slice(0, 4)}-01-01`;
}

export default function UsagePanel({ onClose, token }: Props) {
  const [from, setFrom] = useState(jahresanfang());
  const [to, setTo] = useState(heute());
  const [zeilen, setZeilen] = useState<Zeile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const laden = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(
        `${API_URL}/api/boxes/ki-usage?from=${from}&to=${to}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error('Server-Fehler beim Laden des Verbrauchs.');
      const data = (await res.json()) as Antwort;
      setZeilen(data.boxen);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [from, to, token]);

  useEffect(() => {
    laden();
    // Nur beim ersten Oeffnen automatisch laden.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function csvExport() {
    try {
      const res = await fetch(
        `${API_URL}/api/boxes/ki-usage?from=${from}&to=${to}&format=csv`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ki-verbrauch_${from}_bis_${to}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError('CSV-Export fehlgeschlagen.');
    }
  }

  function schnellwahl(von: string, bis: string) {
    setFrom(von);
    setTo(bis);
  }

  const jahr = heute().slice(0, 4);
  const summeZeitraum = zeilen.reduce((s, z) => s + z.verbrauch, 0);
  const summeGesamt = zeilen.reduce((s, z) => s + z.gesamt, 0);
  const sortiert = [...zeilen].sort((a, b) => b.verbrauch - a.verbrauch);

  return (
    <div className="fixed inset-0 z-30 flex items-start justify-center bg-black/40 p-3 sm:p-6 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-xl border border-surface-border w-full max-w-3xl my-4">
        {/* Kopf */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-surface-border">
          <div>
            <h2 className="font-raleway font-light text-base uppercase tracking-[0.15em] text-ink-primary">
              KI-Freistellungen – Verbrauch
            </h2>
            <p className="text-ink-muted text-xs font-opensans mt-0.5">
              Verbrauch pro System für einen frei wählbaren Zeitraum.
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg border border-surface-border bg-surface-subtle flex items-center justify-center text-ink-muted hover:text-brand-red hover:border-brand-red transition-all"
            title="Schließen"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Zeitraum */}
        <div className="px-5 py-4 border-b border-surface-border space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col text-xs font-opensans text-ink-muted">
              Von
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="mt-1 px-3 py-2 rounded-lg border border-surface-border bg-surface-subtle text-ink-primary font-mono text-sm focus:border-brand-green outline-none"
              />
            </label>
            <label className="flex flex-col text-xs font-opensans text-ink-muted">
              Bis
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="mt-1 px-3 py-2 rounded-lg border border-surface-border bg-surface-subtle text-ink-primary font-mono text-sm focus:border-brand-green outline-none"
              />
            </label>
            <button
              onClick={laden}
              className="px-4 py-2 rounded-lg bg-brand-green text-white text-sm font-medium hover:opacity-90 transition-opacity"
            >
              Anzeigen
            </button>
            <button
              onClick={csvExport}
              className="px-4 py-2 rounded-lg border border-surface-border bg-surface-subtle text-ink-secondary text-sm font-medium hover:border-brand-green hover:text-brand-green transition-colors"
            >
              CSV-Export
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              ['Dieses Jahr', `${jahr}-01-01`, heute()],
              ['Vorjahr', `${+jahr - 1}-01-01`, `${+jahr - 1}-12-31`],
              ['Dieser Monat', `${heute().slice(0, 7)}-01`, heute()],
            ].map(([label, von, bis]) => (
              <button
                key={label}
                onClick={() => schnellwahl(von, bis)}
                className="px-3 py-1 rounded-full border border-surface-border bg-surface-subtle text-ink-muted text-xs hover:border-brand-green hover:text-brand-green transition-colors"
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Tabelle */}
        <div className="px-5 py-4">
          {error && <p className="text-brand-red text-sm font-opensans mb-3">{error}</p>}
          {loading ? (
            <div className="flex items-center justify-center h-32 gap-3">
              <div className="w-5 h-5 border-2 border-brand-green border-t-transparent rounded-full animate-spin" />
              <span className="text-ink-muted text-sm">Lade…</span>
            </div>
          ) : sortiert.length === 0 ? (
            <p className="text-ink-muted text-sm font-opensans text-center py-8">
              Keine Verbrauchsdaten im gewählten Zeitraum.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm font-opensans">
                <thead>
                  <tr className="text-ink-muted text-xs uppercase tracking-wider border-b border-surface-border">
                    <th className="text-left font-medium py-2 pr-3">System</th>
                    <th className="text-right font-medium py-2 px-3">Zeitraum</th>
                    <th className="text-right font-medium py-2 pl-3">Gesamt (kumulativ)</th>
                  </tr>
                </thead>
                <tbody>
                  {sortiert.map((z) => (
                    <tr key={z.id} className="border-b border-surface-border/60">
                      <td className="py-2 pr-3">
                        <span className="text-ink-primary">{z.name}</span>
                        <span className="text-ink-faint text-xs ml-2 font-mono">{z.id}</span>
                      </td>
                      <td className="py-2 px-3 text-right font-mono font-bold text-brand-green">
                        {z.verbrauch.toLocaleString('de-DE')}
                      </td>
                      <td className="py-2 pl-3 text-right font-mono text-ink-secondary">
                        {z.gesamt.toLocaleString('de-DE')}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="font-bold text-ink-primary">
                    <td className="py-2 pr-3">Summe</td>
                    <td className="py-2 px-3 text-right font-mono text-brand-green">
                      {summeZeitraum.toLocaleString('de-DE')}
                    </td>
                    <td className="py-2 pl-3 text-right font-mono">
                      {summeGesamt.toLocaleString('de-DE')}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
